// Shared life loop and controls. A host owns its canvas, geometry, input and persistence.
import { env, clamp, rand, pick, weighted, Cancelled, frame, startRun, tick } from '../engine/core.js';
import { skinEffects, skinsFor } from '../engine/skins.js';
import { OUTFITS } from '../engine/outfits.js';
import { cast, makeCompanion, morph, drawCompanion, spotPoint, spotScale, scaleOf, fits, heightOf, insideMargin, applyRest, resetPose, snapShape, setShape, letGo } from '../engine/companion.js';
import { pace, ACTIVITY, GATHERINGS, gatherings, activity, keepsStill } from '../engine/activity.js';
import { initWorld, discoverLedges, visibleOn, visibleUnder, surfaceBelow, FLOOR, castleLeft, refreshWorld, viewport } from '../engine/world.js';
import { drawStage, drawStageFront, props, clearProps } from '../engine/stage.js';
import * as fx from '../engine/particles.js';
import { travelTo, arrive, fallDown, baseOf } from '../engine/travel.js';
import * as acts from '../engine/acts.js';
import * as together from '../engine/together.js';
import * as fights from '../engine/fights.js';
import { loadAbilities, registerDesigns } from '../engine/abilities.js';
import { applySettings, settingsOf } from '../engine/settings.js';
import { can, attackStyle } from '../engine/catalog.js';
import { pointerInput, pointerAt, travelled, resetPointer } from '../engine/pointer.js';

import { clockNow } from '../engine/clock.js';
import { useClock } from '../engine/clock.js';
import { createClock } from './clock.js';
import { createStorage, loadSettings } from './storage.js';
import { SIDES, leaveOver, placeOutside, comeIn } from './edges.js';
import { createShowMe } from './showme.js';
import { createLanguage, isChoice, AUTO } from './language.js';

const runtimeSlot = Symbol.for('companions.runtime');
const LANGUAGE_WAIT = 3000;                 // ms the runtime waits for its language's table at the start
export async function createRuntime({ canvas, designs, catalog = null, load = null, world, input, storage, clock, languages = {}, options = {} }) {
  if (globalThis[runtimeSlot]) throw new Error('Only one companion runtime may exist on a page. Destroy it before creating another.');
  if (!canvas || !world?.snapshot || !designs || !(Object.keys(designs).length || (catalog?.length && load))) {
    throw new Error('A canvas, designs (or a catalog and load) and world.snapshot() are required.');
  }
  const reservation = {}; globalThis[runtimeSlot] = reservation;
  try {
    await loadAbilities(designs);
    const scheduler = createClock(clock), store = createStorage(storage), events = new Map(), busy = new Set();
    let destroyed = false, started = false, initializing = true, loopFrame = null;
    // The language (language.js): the host's choice, else the saved one, else 'auto'. Its table is
    // in before the runtime is (so a menu draws in it at once), unless it takes longer than
    // LANGUAGE_WAIT: then the runtime starts in English and reports an options change once it's in.
    const lang = createLanguage(languages);
    const languageWanted = options.language ?? store.get('language', AUTO);
    if (options.language !== undefined && !isChoice(options.language)) console.warn(`Companions: language "${options.language}" is unknown; using auto.`);
    const firstTable = lang.use(isChoice(languageWanted) ? languageWanted : AUTO);
    let waiting;
    const late = await Promise.race([firstTable.then(() => false), new Promise(resolve => { waiting = setTimeout(resolve, LANGUAGE_WAIT, true); })]);
    clearTimeout(waiting);
    if (late) firstTable.then(changed => { if (changed && !destroyed) emit('change', { type: 'options' }); });
    let disposal = null;
    let stopInput = null, stopWorld = null;
    useClock(scheduler);
    env.ctx = canvas.getContext('2d');
    // The owner's size (3-8) and the host's canvas pixels per size step: the drawing scale is
    // their product in whole pixels, so every art pixel covers whole canvas pixels.
    const pixelScale = Number(options.pixelScale) > 0 ? Number(options.pixelScale) : 1;
    let size = Number(options.size ?? 5);
    size = Number.isFinite(size) ? clamp(size, env.MIN_S, env.MAX_S) : 5;
    const applySize = () => { env.unit = pixelScale; env.S = pixelScale === 1 ? size : Math.round(size * pixelScale); };
    applySize();
    initWorld(world);
    // Companions the host brings later (the website loads each one when it's needed): `catalog`
    // lists everyone it can bring ([{ id, name, group }], in menu order; `about` may wait for the
    // design), and `load(ids, { moves })` resolves to their designs; with `moves`, the abilities
    // they use are registered too (engine/abilities.js) or can be imported. Without them, `designs`
    // is everyone.
    const listed = (catalog || Object.values(designs)).map(c => ({ id: c.id, name: c.name, group: c.group || 'Others', about: c.about || '' }));
    const known = new Set(listed.map(c => c.id));
    const looks = new Map();
    for (const [id, settings] of Object.entries(options.looks || {})) {
      if (known.has(id)) looks.set(id, settings);
      else console.warn(`Companions: no companion called ${id} in looks; ignored.`);
    }
    const all = {};
    const add = found => {
      for (const [id, design] of Object.entries(found)) {
        if (all[id]) continue;
        all[id] = makeCompanion(design, env.S);
        // Page looks are only a starting point, including for companions brought later. Only a
        // visitor's changes (setCompanion) save them; old individual choices still win too.
        const page = looks.get(id) || {}, defaults = { size: page.size,
          parachute: { color: page.parachute?.color, pattern: page.parachute?.pattern }, outfit: {} };
        if (page.skin !== undefined) {
          if (skinsFor(design).includes(page.skin)) defaults.skin = page.skin;
          else console.warn(`Companions: looks.${id}.skin "${page.skin}" is unknown; ignored.`);
        }
        for (const slot of ['head', 'body']) {
          const key = page.outfit?.[slot];
          if (key === undefined) continue;
          if (design.outfits?.includes(slot) && Object.hasOwn(OUTFITS[slot], key)) defaults.outfit[slot] = key;
          else console.warn(`Companions: looks.${id}.outfit.${slot} "${key}" is unknown; ignored.`);
        }
        applySettings(all[id], loadSettings(store, id, defaults));
      }
    };
    add(designs);
    const withMoves = new Set(Object.keys(all));       // here with their abilities: they can go on screen
    const pending = new Map();                         // 'design:id' or 'moves:id' -> its load, while it runs
    const isReady = (id, { moves = true } = {}) => (moves ? withMoves.has(id) : !!all[id]);
    // Resolves once these companions are here: their designs (for portraits, looks and settings) and,
    // with `moves` (the default), their abilities, so they can go on screen.
    async function ready(ids = [...known], { moves = true } = {}) {
      if (destroyed) throw new Error('This companion runtime was destroyed.');
      const unknown = ids.filter(id => !known.has(id));
      if (unknown.length) throw new Error('Unknown companion: ' + unknown.join(', '));
      const key = id => (moves ? 'moves:' : 'design:') + id;
      const missing = ids.filter(id => !isReady(id, { moves }) && !pending.has(key(id)));
      if (missing.length) {
        if (!load) throw new Error('These companions are not loaded and the host has no load(): ' + missing.join(', '));
        const loading = (async () => {
          const found = await load(missing, { moves });
          if (destroyed) return;
          if (moves) await loadAbilities(found); else registerDesigns(found);
          if (destroyed) return;
          add(found);
          if (moves) for (const id of missing) if (all[id]) withMoves.add(id);
          emit('change', { type: 'loaded', ids: missing, moves });
        })();
        for (const id of missing) pending.set(key(id), loading);
        loading.finally(() => { for (const id of missing) if (pending.get(key(id)) === loading) pending.delete(key(id)); }).catch(() => {});
      }
      await Promise.all(ids.map(id => pending.get(key(id))).filter(Boolean));
      const still = ids.filter(id => !isReady(id, { moves }));
      if (still.length && !destroyed) throw new Error('Could not load: ' + still.join(', '));
    }
    const companion = id => {
      const me = typeof id === 'string' ? all[id] : id;
      if (!me && typeof id === 'string' && known.has(id)) throw new Error(`${id} isn't loaded yet: ready(['${id}']) first.`);
      if (!me || all[me.design?.id] !== me) throw new Error('Unknown companion: ' + id);
      return me;
    };
    const emit = (name, value) => {
      for (const fn of events.get(name) || []) {
        try { fn(value); } catch (e) { console.error(e); }
      }
    };
    const on = (name, fn) => {
      if (!events.has(name)) events.set(name, new Set());
      events.get(name).add(fn); return () => events.get(name)?.delete(fn);
    };
    let alive = options.alive ?? true, chatOpen = false, lastInput = clockNow(), lastKey = 0;
    const state = {
      alive: () => alive,
      chatOpen: () => chatOpen,
      idleFor: () => clockNow() - lastInput,
      // Sleepers wake on a click, a key, or a lot of mouse movement; small nudges don't wake them.
      wakeUp: me => {
        if (me.wakeRequest) { me.wakeRequest = false; return true; }
        const now = clockNow();
        if (now - lastKey < 500) return true;
        return travelled(2000, now) > 600;
      },
    };

    // ---------- running actions ----------
    // A new action first settles the companion's old one: it lands where it was going, and what it
    // had put into the world is cleared. A fall keeps its position instead (snap: false).
    function settle(me, snap) {
      if (snap && me.moving && me.dest && spotPoint(me, me.dest)) arrive(me, me.dest);
      me.moving = false; me.dest = null; me.held = false; me.sleeping = false; me.dancing = false;
      clearProps(me); me.chair = me.bed = me.speaker = me.stand = null;
      if ('restoreHead' in me) { me.outfit = { ...me.outfit, head: me.restoreHead }; delete me.restoreHead; }
      letGo(me);                                   // hanging by its feet: a new action never starts there
      if (me.anchor && !spotPoint(me, me.anchor)) me.anchor = null;
      if (snap && !me.anchor && !me.atHome) {
        const below = surfaceBelow(me.x, me.y - 1), g = below.g, s = env.S;
        arrive(me, { ledge: below.ledge, dx: clamp(me.x, g.x1 + 9 * s, Math.max(g.x1 + 9 * s, g.x2 - 9 * s)) - g.x1, rest: 'stand' });
      }
      resetPose(me);
      if (me.anchor) applyRest(me, me.anchor); else setShape(me, 'normal');
      snapShape(me);
      Object.assign(me, { jump: 0, bob: 0, chute: 0, sink: 0, think: false, typing: false, alpha: me.atHome ? 0 : 1 });
      me.pose.stride = 0;
    }

    const mainBusy = me => busy.has(me);
    // After an action: its own life (aliveLoop), or staying where it is (stayLoop) at Still, while
    // a Show me holds it or while it's only visiting; a visitor whose Show me is over goes again.
    async function thenLive(me, g) {
      if (destroyed || !cast.includes(me)) { me.idle = true; return; }
      if (visitors.has(me) && !showMe.of(me)) { await showMe.leave(g); return; }
      if (!alive || mainBusy(me)) me.idle = true;
      else if (own(me) && !showMe.of(me)) await acts.aliveLoop(g, state);
      else await acts.stayLoop(g);
    }

    // A new run starts once the run it interrupts has stopped (at its next frame) and cleaned up, so
    // that run's cleanup (putting its pose back, say) can't undo the new one. It never waits long:
    // a run stuck on something other than frames is left behind after a moment.
    const STOP_WAIT = 250;
    const stopped = me => Promise.race([me.running, new Promise(r => setTimeout(r, STOP_WAIT))]);

    function run(me, action, { snap = true } = {}) {
      if (destroyed) return;
      const g = startRun(me), before = stopped(me);
      me.idle = false;
      me.running = (async () => {
        await before;
        if (g.n !== me.gen) return;                            // replaced again while it waited
        if (destroyed || !cast.includes(me)) return;
        settle(me, snap);
        try {
          await action(g);
          if (g.n === me.gen) await thenLive(me, g);           // not when it handed itself over (a race, a duel: playWith)
        }
        catch (e) { if (!(e instanceof Cancelled)) console.error(e); }
      })();
    }

    // Companions doing something together. If one gets interrupted (picked up, say), the others
    // go back to their own lives. Their run tokens say so (g.together), so an ability can tell it's
    // part of something bigger and doesn't start a game of its own (Santa's sleigh calling Rudolph).
    function runGroup(members, script) {
      if (destroyed) return;
      const gs = members.map(me => Object.assign(startRun(me), { together: true })), before = Promise.all(members.map(stopped));
      for (const me of members) me.idle = false;
      const group = (async () => {
        await before;
        if (gs.some((g, i) => g.n !== members[i].gen)) {         // one was given something else meanwhile
          gs.forEach((g, i) => { if (members[i].gen === g.n) run(members[i], async () => {}); });
          return;
        }
        for (const me of members) settle(me, true);
        try { await script(gs); }
        catch (e) { if (!(e instanceof Cancelled)) console.error(e); }
        gs.forEach((g, i) => { if (members[i].gen === g.n) run(members[i], async () => {}); });
      })();
      for (const me of members) me.running = group;
    }
    const runPair = (A, B, script) => runGroup([A, B], ([gA, gB]) => script(gA, gB));

    // Free to join in: on screen, waiting around, not asleep, carried, at home or already dancing.
    // Reachable: on screen and not carried, asleep, at home or dancing. Free: also not busy with an action.
    const reachable = me => cast.includes(me) && !me.riding && (!options.ride || me.alpha >= 0.5)
      && !me.sleeping && !me.held && !me.atHome && !me.dancing;
    const free = me => reachable(me) && me.idle;
    // Who takes part in what others or the screen start (parties, games, fights, evil spells,
    // helping against pests, what abilities start with others): not one at Still (activity.js), nor
    // one only visiting for a Show me. The companions of a Show me (showme.js) take part in it and
    // in nothing else meanwhile. joins(me, asker): free for something `asker` starts (null: the screen).
    const visitors = new Set();
    const own = me => !keepsStill(me) && !visitors.has(me);
    const joins = (me, asker = null) => free(me) && (showMe.of(me) ? showMe.of(me) === showMe.of(asker) : own(me));
    // Could play once it's free: the director's clock for games runs while someone could.
    const playsAlong = me => reachable(me) && own(me) && !showMe.of(me);

    // Show me (showme.js): what it needs of the runtime. A visitor comes on screen like a newcomer
    // but isn't a member: the menu's On screen, the saved list and the host's list stay as they were.
    const showMe = createShowMe({
      all, cast, companion, ready, joins, free, emit,
      order: () => listed.map(c => c.id),
      known: id => known.has(id),
      started: () => started && !destroyed,
      destroyed: () => destroyed,
      run: (me, action) => run(me, action),
      runPair: (A, B, script) => runPair(A, B, script),
      startParty: host => startParty(host),
      turnEvil: me => turnEvil(me),
      visit: me => updateOnScreen(me, true, { visit: true }),
      visiting: me => visitors.has(me),
      dismiss: (me, { puff = false } = {}) => { if (visitors.has(me)) updateOnScreen(me, false, { quiet: !puff }); },
    });

    // The party: the host sets it up; when the beat drops, everyone who joins parties dances along.
    // Sleepers wake up to the music and join once they're up, and so does anyone who comes free later.
    const joinParty = (me, P) => run(me, g => together.danceAlong(g, P));
    const joinsParty = me => can(me, 'party.join') && own(me) && !showMe.of(me);
    function startParty(host) {
      run(host, async g => {
        await acts.comeOut(g);
        await together.party(g, P => {
          for (const me of cast) {
            if (me === host || !joinsParty(me)) continue;
            if (me.sleeping) me.wakeRequest = true;
            else if (free(me)) joinParty(me, P);
          }
        });
      });
    }

    // Fights (fights.js): two that fight have a duel. One turned evil goes after the others one by one:
    // those that fight have a duel with it, the rest run away.
    const startDuel = (A, B) => runPair(A, B, (gA, gB) => fights.duel(gA, gB));
    const turnEvil = me => run(me, async g => { await acts.comeOut(g); await fights.turnEvil(g); });
    const hunting = {
      duelWith: (E, T) => { if (joins(T, E)) startDuel(E, T); },
      scare: (T, E) => { if (joins(T, E)) run(T, g => fights.runFrom(g, E)); },
    };

    // A passive mob ran from a pest: the nearest free companion that fights (and has bugs and
    // glitches switched on) comes over. True when someone is coming.
    acts.hooks.helpNeeded = (mite, victim) => {
      const helpers = cast.filter(me => me !== victim && joins(me) && attackStyle(me.design) !== 'flee' && can(me, 'pests'));
      if (!helpers.length) return false;
      const helper = helpers.reduce((a, b) => (Math.hypot(a.x - mite.x, a.y - mite.y) < Math.hypot(b.x - mite.x, b.y - mite.y) ? a : b));
      run(helper, g => acts.helpFight(g, mite));
      return true;
    };

    // Companions standing where others want to play (within `reach` of a player) walk somewhere else
    // (not one at Still, visiting or in a Show me: it stays where it is).
    acts.hooks.makeRoom = (players, reach = 22 * env.S) => {
      for (const me of cast) {
        if (players.includes(me) || me.held || me.sleeping || me.riding || (options.ride && me.alpha < 0.5) || !own(me) || showMe.of(me)) continue;
        if (players.some(p => Math.abs(p.x - me.x) < reach && Math.abs(p.y - me.y) < 10 * env.S)) run(me, g => acts.goNear(g));
      }
    };

    // A companion asks others to play a game of its own (an ability: Ferris racing Clawd). If they're
    // all free, everyone leaves what they're doing and plays script(gs), the asker's run token first.
    // True when it starts.
    acts.hooks.playWith = (me, others, script) => {
      if (!others.length || !others.every(o => joins(o, me))) return false;
      runGroup([me, ...others], script);
      return true;
    };
    // Like playWith, but busy ones are taken along too (Krampus coming for the evil one Santa's
    // list found, which is off hunting): any that are reachable and not in a duel. True when it starts.
    acts.hooks.takeAlong = (me, others, script) => {
      const duel = fights.duelOn(), ok = o => reachable(o) && !duel?.fighters.includes(o)
        && (showMe.of(o) ? showMe.of(o) === showMe.of(me) : own(o));
      if (!others.length || ![me, ...others].every(ok)) return false;
      runGroup([me, ...others], script);
      return true;
    };

    // A host can place companions itself where the runtime would move them on its own (options.place):
    // a newcomer ('new'), one whose spot went away, stopped showing it or leaving it room, or is floor
    // it may not stay on ('lost'), and one the visitor let go of ('dropped'). True when the host did.
    function place(me, why, previous = null) {
      try { return !!options.place?.(me, why, previous); } catch (e) { console.error(e); return false; }
    }

    // ---------- the frame loop ----------
    function follow(me, now) {
      if (options.ride) {
        let spot = me.dest || me.anchor;
        const geom = spot && baseOf(spot).geom();
        const riding = !me.held && !me.atHome && geom && baseOf(spot) !== FLOOR
          && (geom.riding || (geom.y < heightOf(me) && geom.room <= geom.y));
        if (riding) {
          // A wall used for leaning can leave discovery before the surface underneath it does.
          // In that case, stand on the surface itself rather than lose the page spot.
          if (!spotPoint(me, spot)) spot = { ledge: baseOf(spot), dx: clamp(me.x - geom.x1, 0, geom.x2 - geom.x1), rest: 'stand' };
          // End a walk or flight at its destination, then wait on the spot until it showMe again.
          // A fresh run wakes sleepers, but keeps a resting spot's stand, sit or lounge.
          if (!me.riding || me.rideGen !== me.gen) {
            me.riding = true;
            me.lost = false;
            run(me, async g => { arrive(me, spot); while (me.riding) await tick(g); });
            me.rideGen = me.gen;
          }
          // Under something on top of its spot (a dialog, a banner) it waits out of sight: drawn over
          // the page, it would stand on top of that.
          // (Every frame: the run that keeps it waiting starts a frame later and showMe it again.)
          if (geom.covered) me.alpha = 0; else if (me.rideHidden) me.alpha = 1;
          me.rideHidden = !!geom.covered;
          const pt = spotPoint(me, spot);
          if (pt) { me.x = pt.x; me.y = pt.y; }
          return;
        }
        if (me.riding) me.nextVis = 0;                        // check covers and room as soon as it returns
        if (me.rideHidden) { me.rideHidden = false; me.alpha = 1; }
        me.riding = false;
      }
      if (!me.anchor || me.moving || me.held || me.atHome) return;
      const pt = spotPoint(me, me.anchor), hang = me.anchor.rest === 'hang';
      // What it sat on went away or shrank: it falls. Hanging under something, it lets go as soon
      // as that moves.
      if (!pt || (hang && Math.hypot(pt.x - me.x, pt.y - me.y) > 1)) {
        const previous = me.anchor;
        if (hang) letGo(me); else me.anchor = null;
        if (!place(me, 'lost', previous)) run(me, g => fallDown(g), { snap: false });
        return;
      }
      me.x = pt.x; me.y = pt.y;
      if (now > (me.nextVis || 0)) {
        me.nextVis = now + 700;
        const off = pt.y < (hang ? 0 : 6 * me.s) || pt.y > env.H + 2 || pt.x < 0 || pt.x > env.W;
        const shows = hang ? visibleUnder(baseOf(me.anchor), pt.x, pt.y, me.s) : visibleOn(baseOf(me.anchor), pt.x, pt.y, me.s);
        const hidden = off || !shows || !fits(me, baseOf(me.anchor));
        if ((hidden || acts.restless(me)) && place(me, 'lost', me.anchor)) return;
        if (hidden) {
          me.lost = true;                          // covered by a window, or no room left above: it moves somewhere visible
          // (Without a life of its own just now it moves here: Alive off, at Still, a Show me, a visit.)
          if ((!alive || !own(me) || showMe.of(me)) && me.idle) { me.lost = false; run(me, g => travelTo(acts.spots.near(me) || acts.spots.far(me), g)); }
        }
      }
    }

    // It watches the pointer when it's close, else an evil one (fights.js), if there is one.
    function gaze(me) {
      me.gaze = null;
      if (!me.idle || me.sleeping || me.moving) return;
      const pointer = pointerAt(), near = pointer && Math.hypot(pointer.x - me.x, pointer.y - (me.y - 3 * me.s)) <= 200;
      const at = near ? pointer : cast.find(o => o.evil && o !== me && o.alpha > 0.5);
      if (!at) return;
      const dx = at.x - me.x, dy = at.y - (me.y - 3 * me.s);
      me.gaze = Math.abs(dx) > Math.abs(dy) * 0.8 ? (dx < 0 ? 'left' : 'right') : dy < 0 ? 'up' : 'down';
    }

    function blink(me, now) {
      if (now > (me.nextBlink || 0) + 130) { me.pose.blink = false; me.nextBlink = now + rand(2200, 5200); }
      else if (now > (me.nextBlink || 0)) me.pose.blink = true;
    }

    function loop(now) {
      if (!started || destroyed) return;
      refreshWorld();
      resizeCanvas();
      for (const me of cast) {
        blink(me, now); morph(me, now); follow(me, now); gaze(me);
        acts.updateBabies(me, now);
        skinEffects(me, now);
      }
      director(now);
      showMe.frame(now);
      env.ctx.clearRect(0, 0, env.W, env.H);
      drawStage(now);
      for (const me of cast) for (const b of me.babies) drawCompanion(b.c, now);
      for (const me of [...cast].sort((a, b) => (a.design.id === options.topCompanion) - (b.design.id === options.topCompanion))) drawCompanion(me, now);   // host drawing order
      drawStageFront(now);
      fx.draw(now);
      emit('hitRegions', hitRegions());
      emit('frame', now);
      loopFrame = frame(loop);
    }

    // Now and then the companions play together: a party or file basketball. They're decided on one
    // clock for the whole screen, not by a chance for each companion that could start one, so more
    // hosts on screen don't make more parties: one about every 6 to 10 minutes at normal activity,
    // a little rarer than the things each companion does on its own, and rarer the calmer the
    // screen's level (and a host's gatherings option: gatherings() in activity.js). The time only
    // counts while someone awake on screen could play, and once it's due the game starts as soon as
    // players are free. Who throws parties, joins them, shoots or catches comes from each companion's
    // switches (catalog.js).
    const PLAY_FIRST = [3, 6], PLAY_EVERY = [6, 10];             // minutes, at normal activity
    // The games those that pass ok() could play: a party with one of them as the host, or file
    // basketball for a shooter and a catcher.
    function games(ok) {
      const list = [], hosts = cast.filter(me => ok(me) && can(me, 'party.host'));
      if (hosts.length) list.push({ w: 0.8, go: () => startParty(pick(hosts)) });
      const pairs = basketballPairs(me => ok(me) && !me.babies.length);
      if (pairs.length) list.push({ w: 1, go: () => { const [a, b] = pick(pairs); runPair(a, b, together.basketball); } });
      return list;
    }
    function director(now) {
      const dt = Math.min(1000, now - (clocks.last ?? now));
      clocks.last = now;
      const P = together.partyOn();
      if (P) { for (const me of cast) if (me !== P.host && free(me) && joinsParty(me)) joinParty(me, P); return; }
      if (fightDirector(now, dt)) return;                      // a duel, or an evil one about: no games meanwhile
      if (!alive || chatOpen || !games(playsAlong).length) return;
      clocks.play += dt;
      if (clocks.play < clocks.playDue * gatherings().wait) return;
      const game = weighted(games(me => joins(me)), game => game.w);
      if (!game) return;                                       // due: it starts once players are free
      Object.assign(clocks, { play: 0, playDue: minutes(PLAY_EVERY) });
      game.go();
    }

    // Fights now and then: a duel about once in 30 to 60 minutes while two that fight are on screen,
    // an evil spell at most about once in two hours, both rarer the calmer the activity level
    // (activity.js). The time only counts while there's someone to fight. Never during the chat or a
    // party, and switched off ("Alive") an evil one is itself again at once. Everyone free on screen
    // watches a duel, and keeps an eye on an evil one, backing off when it comes close.
    const FIGHT_EVERY = [30, 60], EVIL_EVERY = [110, 150];       // minutes, at normal activity
    const minutes = ([lo, hi]) => rand(lo, hi) * 60000;
    const clocks = { fight: 0, evil: 0, fightDue: minutes(FIGHT_EVERY), evilDue: minutes(EVIL_EVERY),
      play: 0, playDue: minutes(PLAY_FIRST), last: null };
    function fightDirector(now, dt) {
      // Switched off, an evil one is itself again at once, unless a Show me turned it: that plays out.
      if (!alive) for (const me of cast) if (me.evil && !showMe.of(me)) me.evil = null;
      const evil = cast.filter(me => me.evil);
      if (!alive && !evil.length) return false;
      const D = fights.duelOn();
      if (D) { if (alive) for (const me of cast) if (joins(me) && !D.fighters.includes(me)) run(me, g => fights.watch(g, D)); return true; }
      if (chatOpen) return evil.length > 0;
      for (const E of evil) {
        if (free(E) && (own(E) || showMe.of(E))) run(E, g => fights.hunt(g, { victims: cast.filter(o => o !== E && joins(o, E)), ...hunting }));
        if (!alive) continue;
        for (const me of cast) {
          if (me === E || me === E.evil?.target || !joins(me) || now < (me.waryAt || 0)) continue;
          if (Math.abs(me.x - E.x) < 30 * env.S && Math.abs(me.y - E.y) < 12 * env.S) { me.waryAt = now + 5000; run(me, g => fights.keepAway(g, E)); }
        }
      }
      if (!alive) return true;
      const fighters = cast.filter(me => can(me, 'fight.duel') && !me.babies.length && playsAlong(me));
      if (fighters.length >= 2) clocks.fight += dt;
      if (cast.length >= 2 && cast.some(me => can(me, 'fight.evil') && playsAlong(me))) clocks.evil += dt;
      const level = activity().fights;
      if (clocks.fight >= clocks.fightDue * level) {
        const ready = fighters.filter(me => joins(me));
        if (ready.length >= 2) {
          const A = pick(ready);
          Object.assign(clocks, { fight: 0, fightDue: minutes(FIGHT_EVERY) });
          startDuel(A, pick(ready.filter(o => o !== A)));
          return true;
        }
      }
      if (clocks.evil >= clocks.evilDue * level && !evil.length) {
        const ready = cast.filter(me => joins(me) && can(me, 'fight.evil'));
        if (ready.length) {
          Object.assign(clocks, { evil: 0, evilDue: minutes(EVIL_EVERY) });
          turnEvil(pick(ready));
          return true;
        }
      }
      return evil.length > 0;
    }

    // Every pair of a shooter and a catcher among the companions that pass ok().
    const basketballPairs = (ok, list = cast) => list.flatMap(a => (ok(a) && can(a, 'basketball.throw')
      ? list.filter(b => b !== a && ok(b) && can(b, 'basketball.catch')).map(b => [a, b]) : []));


    function hit(pt) {
      for (const me of [...cast].reverse()) {
        const b = me.bbox, pad = 4;
        if (b && me.alpha >= 0.5 && !me.atHome && pt.x >= b.l - pad && pt.x <= b.r + pad && pt.y >= b.t - pad && pt.y <= b.b + pad) return me;
      }
      return null;
    }

    let gesture = null;
    function inputEvent(e) {
      if (destroyed || !started) return false;
      const now = clockNow();
      pointerInput(e, now);                                    // where the pointer is and what the person does with it (pointer.js)
      if (e.type === 'idle') { lastInput = now - e.ms; return; }
      if (e.type === 'out') return;                            // it left this screen: no input
      if (e.type === 'key') lastKey = now;
      if (e.type === 'move') {
        const p = { x: e.x, y: e.y };
        if (gesture && gesture.id === e.pointerId) {
          Object.assign(gesture.ptr, p);
          if (!gesture.dragging && Math.hypot(p.x - gesture.start.x, p.y - gesture.start.y) > 5) {
            gesture.dragging = true; run(gesture.me, g => acts.dragged(g, gesture.ptr, land), { snap: false });
          }
        }
      }
      lastInput = now;
      if (e.type === 'down') {
        const me = hit(e);
        if (!me || gesture) return false;
        gesture = { id: e.pointerId, me, start: e, ptr: { x: e.x, y: e.y, down: true }, dragging: false };
        return true;
      }
      if ((e.type === 'up' || e.type === 'cancel') && gesture?.id === e.pointerId) {
        const { me, ptr, dragging } = gesture; ptr.down = false; gesture = null;
        if (dragging || e.type === 'cancel') return;
        emit('click', { id: me.design.id });
        if (me.sleeping) me.wakeRequest = true;
        else run(me, g => acts.clicked(g));
      }
    }
    const land = g => place(g.me, 'dropped') || fallDown(g);      // let go: the host places it, or it falls
    function hitRegions() {
      return cast.filter(me => me.bbox && me.alpha >= 0.5 && !me.atHome).map(me => {
        const b = me.bbox; return { id: me.design.id, x: b.l - 4, y: b.t - 4, width: b.r - b.l + 8, height: b.b - b.t + 8 };
      });
    }
    // One that isn't here yet comes on screen once it has loaded (a promise), unless it was taken off
    // again meanwhile: the latest call for a companion wins.
    const wanted = new Map();
    function setOnScreen(id, on) {
      if (destroyed) throw new Error('This companion runtime was destroyed.');
      wanted.set(id, !!on);
      if (on && known.has(id) && !withMoves.has(id)) {
        return ready([id]).then(() => { if (!destroyed && wanted.get(id) === true) updateOnScreen(companion(id), true); });
      }
      return updateOnScreen(companion(id), on);
    }
    // Where a newcomer appears: on the floor, or with `startOn: 'surfaces'` on one of the world's
    // surfaces, chosen like any trip (where it fits and showMe, away from the others; not leaning),
    // and on the floor only when there is none.
    // While they first arrive, one without an opening spot of its own leaves the others' opening spots free.
    const onSurface = l => l !== FLOOR && !l.wallSide;
    const reservedFor = me => new Set(Object.entries((initializing && options.initialSpots) || {})
      .filter(([id]) => id !== me.design.id).flatMap(([, spot]) => [].concat(spot?.surfaceId)));
    const newcomerSpot = me => {
      const reserved = reservedFor(me);
      return (options.startOn === 'surfaces' && acts.chooseSpot(me, { filter: l => onSurface(l) && !reserved.has(l.id) }))
        || acts.chooseSpot(me, { only: 'floor' });
    };
    // A newcomer's opening spot from options.initialSpots: its surface, or the first of several that the
    // world has now (and, with checkInitialSpots, that is wide enough, leaves room for it, showMe and is
    // nobody else's). Null: it starts as any newcomer.
    function openingFor(me, initial) {
      const ledges = discoverLedges();
      for (const id of [].concat(initial.surfaceId)) {
        const ledge = ledges.find(l => l.id === id), g = ledge?.geom();
        if (!g) continue;
        const opening = { ledge, dx: (g.x2 - g.x1) * clamp(initial.at ?? 0.5, 0, 1), rest: initial.rest ?? 'stand' };
        if (!options.checkInitialSpots) return opening;
        const s = spotScale(me, opening), half = g.inside ? insideMargin(me, s) : 9 * s;
        const taken = cast.some(o => o !== me && [o.anchor, o.dest].some(a => a && baseOf(a).id === ledge.id));
        if (g.x2 - g.x1 < 2 * half || !fits(me, ledge) || taken) continue;
        opening.dx = clamp(g.x1 + opening.dx, g.x1 + half, g.x2 - half) - g.x1;
        const at = g.x1 + opening.dx;
        const crowded = cast.some(o => o !== me && o.alpha > 0 && [{ x: o.x, y: o.y }, o.dest && spotPoint(o, o.dest)]
          .filter(Boolean).some(p => Math.hypot(p.x - at, p.y - g.y) < 20 * s));
        if (!crowded && visibleOn(ledge, at, g.y, s)) return opening;
      }
      return null;
    }
    const members = () => cast.filter(c => !visitors.has(c)).map(c => c.design.id);
    // On or off screen. A visit (a Show me's, showme.js) is on screen without being a member, and
    // reported as `visit` changes; put on screen meanwhile, the visitor stays as a member.
    function updateOnScreen(me, on, { quiet = false, visit = false } = {}) {
      if (on && !visit && visitors.has(me)) {
        visitors.delete(me);
        if (!initializing) store.set('onScreen', JSON.stringify(members()));
        emit('change', { type: 'onScreen', id: me.design.id, on: true });
        // From waiting (or walking off: it's back on the floor at once) to a life of its own.
        if (!showMe.of(me) && !me.held) run(me, async () => {});
        return;
      }
      if (on === cast.includes(me)) return;
      if (on && visit) visitors.add(me);
      if (on && !withMoves.has(me.design.id)) throw new Error(`${me.design.id} can't go on screen before its moves have loaded.`);
      if (!on) {
        me.gen++;                                              // stops whatever it was doing
        me.evil = null;
        clearProps(me); me.babies.length = 0;
        if (gesture?.me === me) gesture = null;
        if (!quiet) fx.puff(me.x, me.y - 4 * me.s, me.s);
        cast.splice(cast.indexOf(me), 1);
      } else if (quiet) {
        cast.push(me); me.atHome = false;                      // the host command places it
      } else {
        cast.push(me);
        const initial = initializing ? options.initialSpots?.[me.design.id] : null;
        const opening = initial ? openingFor(me, initial) : null;
        me.anchor = null; me.atHome = false;
        if (!opening && place(me, 'new')) snapShape(me);       // the host put it somewhere (no puff, no hop)
        else {
          const spot = opening || newcomerSpot(me) || { ledge: FLOOR, dx: rand(20 * env.S, castleLeft() - 20 * env.S), rest: 'stand' };
          arrive(me, spot); snapShape(me);
          if (opening && options.checkInitialSpots) me.openingPlaced = true;
          if (!opening) fx.puff(me.x, me.y - 4 * me.s, me.s);
          if (started || !initializing) run(me, async g => { await acts.happyHop(g, 1); });
        }
      }
      if (visitors.has(me)) {
        if (!on) visitors.delete(me);
        emit('change', { type: 'visit', id: me.design.id, on });
        return;
      }
      if (!initializing) store.set('onScreen', JSON.stringify(members()));
      emit('change', { type: 'onScreen', id: me.design.id, on });
    }

    function resizeCanvas() {
      const view = viewport(), dpr = view.dpr ?? globalThis.devicePixelRatio ?? 1;
      env.W = view.width; env.H = view.height;
      const width = Math.round(env.W * dpr), height = Math.round(env.H * dpr);
      canvas.style.width = env.W + 'px'; canvas.style.height = env.H + 'px';
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      env.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      env.ctx.imageSmoothingEnabled = false;
    }
    function setCompanion(id, settings) {
      if (destroyed) throw new Error('This companion runtime was destroyed.');
      const me = companion(id), old = settingsOf(me), wasStill = keepsStill(me);
      applySettings(me, { ...old, ...settings,
        outfit: { ...old.outfit, ...settings.outfit }, parachute: { ...old.parachute, ...settings.parachute },
        does: settings.does ?? old.does });
      store.set('companion.' + me.design.id, JSON.stringify(settingsOf(me)));
      // Still (activity.js) applies at once: it stops where it is (an evil spell ends with it); back
      // from Still, its own life starts again once it rests. A Show me playing on it goes on.
      if (keepsStill(me) !== wasStill && cast.includes(me) && !showMe.of(me)) {
        if (wasStill ? me.idle : !me.held) { if (!wasStill) me.evil = null; run(me, async () => {}); }
      }
      emit('change', { type: 'companion', id: me.design.id });
    }
    function setOptions(next) {
      if (destroyed) throw new Error('This companion runtime was destroyed.');
      if (next.size != null && Number.isFinite(Number(next.size))) {
        size = clamp(Number(next.size), env.MIN_S, env.MAX_S); applySize(); refreshWorld();
        for (const me of Object.values(all)) me.s = me.anchor ? spotScale(me, me.anchor) : scaleOf(me);
      }
      for (const [key, levels] of [['activity', ACTIVITY], ['gatherings', GATHERINGS]]) {
        if (!Object.hasOwn(levels, next[key])) continue;
        pace[key] = next[key];
        if (key === 'activity') store.set(key, next[key]);   // gatherings is the host's own: never saved
      }
      if (typeof next.alive === 'boolean' && alive !== next.alive) {
        alive = next.alive;
        for (const me of cast) run(me, async () => {});
      }
      if (Array.isArray(next.companions)) {
        const list = next.companions.filter(id => known.has(id));
        for (const id of known) wanted.set(id, list.includes(id));
        const apply = () => {
          for (const [id, me] of Object.entries(all)) {
            if (withMoves.has(id) && !(visitors.has(me) && wanted.get(id) !== true)) updateOnScreen(me, wanted.get(id) === true);
          }
        };
        const missing = list.filter(id => !withMoves.has(id));
        apply();                                             // those that are here now; the others once they have loaded
        if (missing.length) ready(missing).then(() => { if (!destroyed) apply(); }, e => console.error(e));
      }
      // The language: saved, and an options change again once its table is in (language.js).
      if (next.language !== undefined) {
        if (isChoice(next.language)) {
          store.set('language', next.language);
          lang.use(next.language).then(changed => { if (changed && !destroyed) emit('change', { type: 'options' }); });
        } else console.warn(`Companions: language "${next.language}" is unknown; ignored.`);
      }
      emit('change', { type: 'options' });
    }
    // The screen's activity level: the host's, else the saved one. The gatherings level is only ever
    // the host's (no menu offers it any more), so an old saved one doesn't linger unseen.
    const activitySaved = options.activity ?? store.get('activity', 'normal');
    pace.activity = Object.hasOwn(ACTIVITY, activitySaved) ? activitySaved : 'normal';
    pace.gatherings = Object.hasOwn(GATHERINGS, options.gatherings) ? options.gatherings : 'normal';
    resizeCanvas();
    let saved = options.companions;
    if (!saved) { try { saved = JSON.parse(store.get('onScreen', 'null')); } catch { /* defaults */ } }
    if (!Array.isArray(saved)) saved = Object.keys(all).slice(0, 1);
    for (const id of saved) if (withMoves.has(id)) updateOnScreen(all[id], true);
    initializing = false;
    // Saved ones the host didn't bring yet come once they have loaded.
    for (const id of saved) if (known.has(id) && !withMoves.has(id)) setOnScreen(id, true)?.catch?.(e => console.error(e));
    // language: the choice ('auto' or a code); languageInUse: the language whose texts are in use
    // ('en' until a table is in, and when it can't be had); systemLanguage: what 'auto' picks here.
    const getOptions = () => ({ size, alive, activity: pace.activity, gatherings: pace.gatherings, companions: members(),
      language: lang.choice, languageInUse: lang.inUse(), systemLanguage: lang.system() });

    // ---------- moving between runtimes ----------
    // A host with one runtime per screen (the desktop app, one per monitor) moves companions
    // between them: one leaves over an edge here and comes in over an edge there, or is handed
    // over while held. Settings are the host's to carry along (getCompanion / setCompanion).
    const freeToGo = me => free(me) && own(me) && !showMe.of(me) && !busy.has(me) && !me.babies.length && !me.evil && !together.partyOn() && !fights.duelOn();
    // It goes off screen over `side` ('left', 'right', 'top', 'bottom') its own way and is then
    // off this runtime. Resolves to where it left ({ x, y }), or null when it stays: it is busy
    // (a game, a party, held, asleep), or something interrupts it on the way.
    function leave(id, side) {
      const me = companion(id);
      if (destroyed || !started || !SIDES.has(side) || !freeToGo(me)) return Promise.resolve(null);
      let finish;
      const result = new Promise(resolve => { finish = resolve; });
      run(me, async g => {
        try {
          await leaveOver(g, side);
          const at = { x: me.x, y: me.y };
          updateOnScreen(me, false, { quiet: true });
          finish(at);
        } finally { finish(null); }
      });
      me.running.then(() => finish(null));                     // replaced before it began
      return result;
    }
    // It comes in over `side`, `at` 0..1 along the top or bottom (from the side it walks in on
    // the floor). False when it is already here.
    function enter(id, { side, at = 0.5 } = {}) {
      const me = companion(id);
      if (destroyed || !SIDES.has(side) || cast.includes(me)) return false;
      updateOnScreen(me, true, { quiet: true });
      placeOutside(me, side, at);
      run(me, g => comeIn(g, side), { snap: false });
      return true;
    }
    // On screen held at (x, y) by the pointer `pointerId`, as if picked up there (dragged in
    // from another screen): it follows that pointer's moves and drops when it lets go.
    function hold(id, { x, y, pointerId }) {
      const me = companion(id);
      if (destroyed || !Number.isFinite(x) || !Number.isFinite(y)) return false;
      if (!cast.includes(me)) updateOnScreen(me, true, { quiet: true });
      me.anchor = null; me.x = x; me.y = y + (me.shape.bh + me.shape.leg) * me.s;
      const ptr = { x, y, down: true };
      gesture = { id: pointerId, me, start: { x, y }, ptr, dragging: true };
      run(me, g => acts.dragged(g, ptr), { snap: false });
      return true;
    }
    // Off screen at once without a trace (handed to another runtime).
    function remove(id) {
      if (destroyed) return;
      updateOnScreen(companion(id), false, { quiet: true });
    }
    function start() {
      if (destroyed) throw new Error('This companion runtime was destroyed.');
      if (started) return;
      started = true; scheduler.resume();
      stopInput = input?.subscribe(inputEvent);
      stopWorld = world.subscribe?.(() => { refreshWorld(); resizeCanvas(); });
      loopFrame = frame(loop);
      for (const me of cast) if (!me.running) {
        const placed = me.openingPlaced; delete me.openingPlaced;
        run(me, async g => { if (!placed) await acts.happyHop(g, 1); });
      }
    }
    function stop() {
      if (!started) return;
      scheduler.pause(); started = false;
      scheduler.cancel(loopFrame); loopFrame = null;
      stopInput?.(); stopWorld?.(); stopInput = stopWorld = null;
      resetPointer();                                          // not listening: where the pointer is is unknown until it moves again
      if (gesture) { gesture.ptr.down = false; gesture = null; }
    }
    function destroy() { return disposal ??= dispose(); }
    async function dispose() {
      stop(); destroyed = true; alive = false;
      showMe.dispose(); lang.reset();
      for (const me of Object.values(all)) { me.gen++; me.evil = null; }
      scheduler.flush();
      await Promise.all(Object.values(all).map(stopped));
      props.clear(); fx.clear(); cast.length = 0;
      for (const name of ['helpNeeded', 'makeRoom', 'playWith', 'takeAlong']) delete acts.hooks[name];
      env.ctx.clearRect(0, 0, env.W, env.H);
      emit('hitRegions', []); emit('destroy'); events.clear();
      scheduler.clear(); useClock(null); initWorld(null); env.ctx = null; env.unit = 1;
      if (globalThis[runtimeSlot] === reservation) delete globalThis[runtimeSlot];
    }
    return { start, stop, pause: stop, resume: start, destroy, setOptions, getOptions, setCompanion, setOnScreen, on,
      showMe: showMe.api,
      // A description the host's catalog left out comes with the design, once that's here.
      ready, isReady, catalog: () => listed.map(c => ({ ...c, about: c.about || all[c.id]?.design.about || '' })),
      getCompanion: id => settingsOf(companion(id)), getCompanions: () => [...known],
      getPreference: (key, fallback) => store.get(key, fallback), setPreference: (key, value) => store.set(key, value),
      // Host extensions (the preview's test actions and chat) use the same action lifecycle.
      commands: { run, runPair, runGroup, startParty, startDuel, turnEvil, setBusy(id, on) {
        const me = companion(id); if (on) busy.add(me); else busy.delete(me); chatOpen = busy.size > 0;
      }, leave, enter, hold, remove },
      inspect: { all, cast, state, clocks, props, free },   // free(me): on screen, idle, awake, not held or at home
    };
  } catch (e) {
    cast.length = 0; props.clear(); fx.clear(); useClock(null); initWorld(null); env.ctx = null; env.unit = 1;
    for (const name of ['helpNeeded', 'makeRoom', 'playWith', 'takeAlong']) delete acts.hooks[name];
    if (globalThis[runtimeSlot] === reservation) delete globalThis[runtimeSlot];
    throw e;
  }
}
